import React from 'react';
import { DatePicker as AntdDatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const DEFAULT_DATE_FORMATS = [
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'DD-MM-YYYY',
  'D/M/YYYY',
  'D-M-YYYY',
  'YYYY/MM/DD',
];

const MONTH_FORMATS = ['YYYY-MM', 'MM/YYYY', 'MM-YYYY', 'M/YYYY', 'M-YYYY', 'YYYY/MM'];
const YEAR_FORMATS = ['YYYY'];

export const parseFlexibleDate = (
  rawText?: string,
  picker?: DatePickerProps['picker']
): Dayjs | undefined => {
  if (!rawText) return undefined;
  const cleaned = rawText.trim();
  if (!cleaned) return undefined;

  if (picker === 'year') {
    const yearMatch = cleaned.match(/\b(19\d{2}|20\d{2})\b/);
    if (yearMatch) {
      const d = dayjs(yearMatch[1], 'YYYY', true);
      if (d.isValid()) return d;
    }
    for (const fmt of YEAR_FORMATS) {
      const d = dayjs(cleaned, fmt, true);
      if (d.isValid()) return d;
    }
    return undefined;
  }

  if (picker === 'month') {
    for (const fmt of MONTH_FORMATS) {
      const d = dayjs(cleaned, fmt, true);
      if (d.isValid()) return d;
    }
    for (const fmt of MONTH_FORMATS) {
      const d = dayjs(cleaned, fmt);
      if (d.isValid()) return d;
    }
    return undefined;
  }

  for (const fmt of DEFAULT_DATE_FORMATS) {
    const d = dayjs(cleaned, fmt, true);
    if (d.isValid()) return d;
  }
  for (const fmt of DEFAULT_DATE_FORMATS) {
    const d = dayjs(cleaned, fmt);
    if (d.isValid()) return d;
  }
  const fallback = dayjs(cleaned);
  return fallback.isValid() ? fallback : undefined;
};

const BaseDatePicker = React.forwardRef<any, DatePickerProps>((props, ref) => {
  const { format, onChange, disabled, picker, ...restProps } = props;

  // Mặc định cho phép gõ/dán đa định dạng nhưng hiển thị chuẩn YYYY-MM-DD (nếu là chọn ngày)
  const resolvedFormat = React.useMemo(() => {
    if (picker && picker !== 'date') {
      return format;
    }
    if (!format || format === 'YYYY-MM-DD') {
      return DEFAULT_DATE_FORMATS;
    }
    if (typeof format === 'string') {
      return [format, ...DEFAULT_DATE_FORMATS.filter((f) => f !== format)];
    }
    return format;
  }, [format, picker]);

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const pastedText = e.clipboardData?.getData('text');
    if (!pastedText) return;

    const parsed = parseFlexibleDate(pastedText, picker);
    if (parsed && parsed.isValid()) {
      e.preventDefault();
      e.stopPropagation();

      const primaryFormat =
        typeof format === 'string'
          ? format
          : picker === 'month'
          ? 'YYYY-MM'
          : picker === 'year'
          ? 'YYYY'
          : 'YYYY-MM-DD';

      onChange?.(parsed as any, parsed.format(primaryFormat));
    }
  };

  return (
    <div onPaste={handlePaste} style={{ display: 'contents' }}>
      <AntdDatePicker
        ref={ref}
        picker={picker}
        disabled={disabled}
        format={resolvedFormat}
        onChange={onChange}
        {...restProps}
      />
    </div>
  );
});

BaseDatePicker.displayName = 'DatePicker';

export const DatePicker = Object.assign(BaseDatePicker, {
  RangePicker: AntdDatePicker.RangePicker,
});

